
import React, { useState } from "react";
import useTheme from "../../../../hooks/useTheme";

import { Card, Icon } from "semantic-ui-react"
import { AppColumn, AppRow, Wrap } from "../../../UI/Layouts";
import AppText from "../../../UI/Texts/AppTexts";
import { SizedBox } from "../../../UI/Sizes";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { useSelector } from "react-redux";
import { findChatForJob, openInNewTab } from "../../../../utils/helperFunctions";
import { ensureRelationshipChat } from "../../../../utils/chatApi";
import { getUnreadCountForJob } from "../../../Chats/Messages/messageHelpers";
import axios from "axios";
import { UPDATE_CANDIDATE_DOCUMENTS } from "../../../../utils/Controller/URLConstants";
import { AppOutlineButton, AppPrimaryButton, PrimaryChip } from "../../../UI/CustomButtons";
import { uploadFileWithDetails } from "../../../../utils/firebase/storageFunctions";
import AppPrimaryTextfield from "../../../UI/TextInput";
import { SelectOrUploadFile } from "../../../UI/UploadFile";
import { DOCUMENT_IDS, CANDIDATE_DOCUMENT_IDS, getDocumentNameType } from "../../../../utils/Constants/documentIDs";


const AppliedJobActions = ({ candidateData, onSubmited, showToast }) => {
    const theme = useTheme();

    const { id: candidateID, jobId } = candidateData;

    const { currentUser, chats } = useSelector((state) => state.auth);
    const currentJobChat = findChatForJob(chats, {
        jobId,
        relationshipId: candidateData?.relationshipId,
        workplaceId: candidateData?.workplaceId ?? candidateData?.tenantId,
    });
    // The synced chat list only contains chats this admin already belongs
    // to, so a candidate they've never messaged has no currentJobChat yet
    // even though the relationship (and its chat) may already exist.
    const canMessage = !!currentJobChat || !!candidateData?.relationshipId;


    const { isMobile, isTablet } = useResponsive();

    const [showFileUpload, setShowFileUpload] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [rawFileData, setFile] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState(DOCUMENT_IDS.CV);



    const [isSending, setIsSending] = useState(false);
    const [isOpeningChat, setIsOpeningChat] = useState(false);


    const countData = () => {
        if (!currentJobChat) {
            return { count: 0, lastMessageAt: null, lastMessage: null };
        }

        const myProfile = currentJobChat?.profilesData?.[currentUser?.uid];
        const lastMessage = currentJobChat?.lastMessage;
        const count = getUnreadCountForJob(myProfile, jobId);

        return {
            "count": count,
            "lastMessageAt": lastMessage?.createdAt,
            "lastMessage": lastMessage?.content,
        };
    }

    const documentTypes = CANDIDATE_DOCUMENT_IDS;


    const hasNewMessage = countData().count > 0;

    async function uploadAndSendDocument({ type }) {
        try {

            if (rawFileData && !isSending) {
                setIsSending(true);

                let data = {
                    jobId: jobId,
                    email: currentUser?.email ?? candidateData?.candidate?.email ?? candidateData?.email,
                    candidateID: candidateID,
                };

                let documents = {};

                if (rawFileData.type === 'local') {
                    try {
                        const userFileName = (currentUser ?? candidateData).email.toLowerCase().replace("@", "_");

                        const newFileData = await uploadFileWithDetails({
                            fileData: rawFileData.file,
                            folderPath: `users/user_documents/${userFileName}/others`,
                            onProgress: (_) => { },
                            type: selectedDocumentType,
                        });

                        documents[newFileData.id] = Object.assign({ ...newFileData }, {
                            fileName: fileName ?? newFileData.fileName,
                            type: type,
                        })

                    } catch (error) {
                        console.log("FAILED TO UPLOAD ", error);
                    }
                } else {
                    const fileData = rawFileData.file;

                    documents[fileData.id] = Object.assign({ ...fileData }, {
                        type: type,
                    })
                }

                if (Object.keys(documents).length > 0) {
                    data['documents'] = JSON.stringify(documents);

                    const response = await axios.post(UPDATE_CANDIDATE_DOCUMENTS, data);


                    if (response.data.status === 0) {

                        const jobCandidateData = response.data['jobCandidate'];


                        setShowFileUpload(false);
                        setFileName(null);
                        setFile(null);

                        onSubmited(jobCandidateData, data);


                        showToast("Dokumentet uppladdat", { type: 'success' });
                    }
                }


            }

        } catch (error) {
            console.log(error);
            showToast("Något gick fel", { type: 'error' });
        }

        setIsSending(false);
    }

    const prepareAndNavigateToChat = async () => {
        if (currentJobChat) {
            openInNewTab(`/messages/${currentJobChat.chatId}?thread=${jobId}`);
            return;
        }
        if (isOpeningChat) return;
        setIsOpeningChat(true);
        try {
            const chat = await ensureRelationshipChat(candidateData?.relationshipId);
            if (chat?.chatId) {
                // No job thread exists yet for a freshly-ensured chat, so
                // open the conversation itself rather than a ?thread=jobId
                // link that would point at a message that was never sent.
                openInNewTab(`/messages/${chat.chatId}`);
            } else {
                showToast("Något gick fel", { type: 'error' });
            }
        } finally {
            setIsOpeningChat(false);
        }
    };


    return (<AppColumn>
        <AppRow mainAxisAlignment={isMobile ? 'space-between' : 'start'} style={{
            width: '100%',
        }}>
            {!showFileUpload && <AppRow>
                <AppRow onClick={() => {
                    setShowFileUpload(!showFileUpload);
                }} style={{
                    cursor: 'pointer',
                }} >
                    <Icon name="file alternate" style={{ paddingTop: '0.28rem' }} />
                    <SizedBox width={theme.spacing.elementPaddingSmall} />
                    <AppText.Caption textDecoration='underline'>{"Lägg till dokument"}</AppText.Caption>
                </AppRow>

                <SizedBox width={isMobile ? theme.spacing.elementPadding : theme.spacing.contentPadding} />
            </AppRow>}

            {canMessage && (<div style={{
                position: 'relative',
            }}>

                <AppRow onClick={isOpeningChat ? undefined : prepareAndNavigateToChat} style={{
                    cursor: isOpeningChat ? 'default' : 'pointer',
                    opacity: isOpeningChat ? 0.6 : 1,
                }}>
                    <Icon name={isOpeningChat ? "circle notch" : "comment alternate"} loading={isOpeningChat} style={{ paddingTop: '0.28rem' }} />
                    <SizedBox width={theme.spacing.elementPaddingSmall} />
                    <AppText.Caption textDecoration='underline'>{"Skicka meddelande"}</AppText.Caption>
                </AppRow>

                {hasNewMessage &&
                    <div style={{
                        top: '-4%',
                        backgroundColor: 'red',
                        right: '-1.2rem',
                        borderRadius: '50%',
                        height: '16px',
                        width: '16px',
                        margin: 0,
                        position: 'absolute',
                        border: `${isMobile ? '1px' : '2px'} solid ${theme.colors.white}`,

                    }}
                    />}
            </div>)}
        </AppRow>

        {showFileUpload && (<AppColumn style={{
            width: (!isMobile || isTablet) && '80%',
        }}>
            <SizedBox height={theme.spacing.contentPadding} />
            <Card fluid style={{
                padding: theme.spacing.elementPadding,
                borderRadius: theme.radius.default,
                border: "none",
                boxShadow: "0 10px 15px rgba(0, 0, 0, 0.2)"
            }} >
                <AppPrimaryTextfield value={fileName} onChange={(v) => { setFileName(v) }} capitalizedText="sentence" labelText={'Dokument name'} hintText={'Ange Dokument name'} />
                <SizedBox height={theme.spacing.elementPadding} />
                <SelectOrUploadFile id={'file'} onFileChange={(file, serverMediaFile) => {

                    if (file) {
                        setFile({
                            file: file,
                            type: 'local',
                        });

                        setFileName(file.name);
                    } else if (serverMediaFile) {
                        setFile({
                            file: serverMediaFile,
                            type: 'server',
                        })
                        setFileName(serverMediaFile.fileName);

                        setSelectedDocumentType(serverMediaFile?.type ?? DOCUMENT_IDS.CV);

                    }
                }} title={"Välj eller välj dokument"} />
                <SizedBox height={theme.spacing.contentElementPadding} />

                <Wrap>
                    {documentTypes.map((type) => {
                        const selected = selectedDocumentType === type;

                        const nameType = getDocumentNameType(type)

                        return (<PrimaryChip
                            selected={selected}
                            secondary
                            text={nameType}
                            onClick={() => {
                                setSelectedDocumentType(type);
                            }}
                        />)
                    })}

                </Wrap>

                <SizedBox height={theme.spacing.elementPadding} />


                <AppRow mainAxisAlignment={'end'}>
                    <AppOutlineButton size='large' style={{
                        borderRadius: theme.radius.button,
                    }} onClick={() => {
                        setShowFileUpload(false);
                    }} >{"Avbryt"}</AppOutlineButton>
                    <SizedBox width={theme.spacing.elementPadding} />

                    <AppPrimaryButton loading={isSending} size='large' style={{
                        borderRadius: theme.radius.button,
                    }} onClick={() => {
                        uploadAndSendDocument({ type: selectedDocumentType });
                    }} >{"Skicka"}</AppPrimaryButton>

                </AppRow>
            </Card>
            <SizedBox height={theme.spacing.contentElementPadding} />
        </AppColumn>)}
    </AppColumn>)
}


export default AppliedJobActions;